import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useWhiteboardStore, BranchCreationResult } from '../store/useWhiteboardStore';
import { StickyNote, InheritanceType } from '../types';
import { uploadFile } from '../utils/fileUploadApi';
import { getRandomGlassmorphicColorExcluding } from '../utils/colorPalette';
import { trackEvent } from '../utils/analytics';
import { useQuotaEnforcement } from '../hooks/useQuotaEnforcement';
import AlertModal from './AlertModal';

/**
 * SideChatPane Component
 * 
 * Features:
 * - Chat interface for AI conversations
 * - File upload and drag & drop support
 * - Web search integration
 * - Branch creation from messages
 * - Copy/merge functionality
 * - DOTTED UNDERLINE BRANCHING: Headings (h1-h6) and bold text (**text**) in AI messages 
 *   now have dotted green underlines that allow creating branches directly.
 *   This provides a clean, non-intrusive way to create branches while preserving
 *   natural text selection and markdown formatting.
 * 
 * Dotted Underline Branching:
 * - Dotted green underlines appear under headings and bold text
 * - Underlines become solid on hover for clear visual feedback
 * - Click anywhere on the underlined text to create a branch
 * - Tooltips show "Create branch: [text content]"
 * - Smart focus management: Click → Create branch → Focus textarea when complete
 * - Preserves natural text selection behavior
 * - Maintains all markdown formatting including indentation
 * - No visual clutter or line break issues
 * - Feature can be toggled on/off via inlineClickableHeadingsEnabled state
 */

import BranchCreationModal from './BranchCreationModal';
import CopyMergeModal from './CopyMergeModal';
import MermaidRenderer from './MermaidRenderer';
import ThoughtsDisplay from './ThoughtsDisplay';
import './SideChatPane.css';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { authenticatedApiPost } from '../utils/apiWithAuth';
import { useFeatureAccess } from '../hooks/useQuota';
import { useAuth } from '../hooks/useAuth';


// Utility to extract headers and list items from markdown text
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import { visit } from 'unist-util-visit';

// Get character limit from environment variable with fallback to 1000
const CHARACTER_LIMIT = parseInt(process.env.REACT_APP_CHARACTER_LIMIT || '1000', 10);

// Utility function to recursively extract text from React children
function extractTextFromChildren(children: React.ReactNode): string {
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children);
  }
  
  if (Array.isArray(children)) {
    return children.map(extractTextFromChildren).join('');
  }
  
  if (React.isValidElement(children)) {
    return extractTextFromChildren((children.props as any).children);
  }
  
  return '';
}



interface MarkdownListSummary {
  headers: Array<{ display: string; full: string }>;
  listItems: Array<{ display: string; full: string }>;
}

function extractMarkdownListSummary(markdown: string): MarkdownListSummary {
  const headers: Array<{ display: string; full: string }> = [];
  const listItems: Array<{ display: string; full: string }> = [];
  const tree = unified().use(remarkParse).parse(markdown);
  
  // First pass: collect all headings and bold text with their full content
  const headingNodes: Array<{ node: any; text: string; depth: number }> = [];
  const boldNodes: Array<{ node: any; text: string }> = [];
  
  visit(tree, (node: any) => {
    if (node.type === 'heading' && node.depth >= 1 && node.depth <= 6) {
      // Extract all text content from the heading, including nested elements
      let fullText = '';
      
      visit(node, (child: any) => {
        if (child.type === 'text') {
          fullText += child.value;
        } else if (child.type === 'strong') {
          // For bold text, extract the text content
          const boldText = child.children?.map((c: any) => c.value || '').join('').trim();
          if (boldText) {
            fullText += boldText;
          }
        } else if (child.type === 'emphasis') {
          // For italic text, extract the text content
          const italicText = child.children?.map((c: any) => c.value || '').join('').trim();
          if (italicText) {
            fullText += italicText;
          }
        }
      });
      
      fullText = fullText.trim();
      if (fullText) {
        headingNodes.push({ node, text: fullText, depth: node.depth });
      }
    } else if (node.type === 'strong') {
      // Extract bold text nodes
      let boldText = '';
      visit(node, (child: any) => {
        if (child.type === 'text') {
          boldText += child.value;
        }
      });
      
      boldText = boldText.trim();
      if (boldText) {
        boldNodes.push({ node, text: boldText });
      }
    }
  });
  
  // If there are headings or bold text, show them as clickables
  if (headingNodes.length > 0 || boldNodes.length > 0) {
    // Process headings
    headingNodes.forEach(({ text, depth }) => {
      // Remove colons and clean up the text
      let cleanText = text;
      const colonIdx = cleanText.indexOf(':');
      if (colonIdx !== -1) {
        cleanText = cleanText.slice(0, colonIdx).trim();
      }
      
      if (cleanText) {
        // Create display text (truncate if too long)
        const displayText = cleanText.length > 25 ? cleanText.slice(0, 22) + '...' : cleanText;
        headers.push({ display: displayText, full: cleanText });
      }
    });
    
    // Process bold text
    boldNodes.forEach(({ text }) => {
      // Remove colons and clean up the text
      let cleanText = text;
      const colonIdx = cleanText.indexOf(':');
      if (colonIdx !== -1) {
        cleanText = cleanText.slice(0, colonIdx).trim();
      }
      
      if (cleanText) {
        // Create display text (truncate if too long)
        const displayText = cleanText.length > 25 ? cleanText.slice(0, 22) + '...' : cleanText;
        headers.push({ display: displayText, full: cleanText });
      }
    });
  } else {
    // Only extract list items if there are no headings or bold text at all
    visit(tree, (node: any) => {
      if (node.type === 'listItem') {
        let text = '';
        visit(node, (child: any) => {
          if (child.type === 'text') text += child.value + ' ';
          else if (child.type === 'strong') {
            const boldText = child.children?.map((c: any) => c.value || '').join('').trim();
            if (boldText) text += boldText + ' ';
          }
        });
        text = text.trim();
        if (text) {
          const colonIdx = text.indexOf(':');
          if (colonIdx !== -1) {
            text = text.slice(0, colonIdx).trim();
          } else {
            text = text.split(/\s+/).slice(0, 3).join(' ');
          }
          // Store full text and create display text
          const fullText = text;
          const displayText = text.length > 25 ? text.slice(0, 22) + '...' : text;
          listItems.push({ display: displayText, full: fullText });
        }
      }
    });
  }
  
  return { headers, listItems };
}

interface Message {
  sender: 'user' | 'ai';
  text: string;
}

interface EnhancedMessage {
  sender: 'user' | 'ai';
  text: string;
  suggestions?: string[];
  analysis?: {
    intent: string;
    tone: string;
    confidence: number;
  };
  topic?: string;
  thought_thread?: string[];
}

interface SideChatPaneProps {
  isOpen: boolean;
  onToggle: () => void;
  selectedNote: StickyNote | null;
  onBranchCreated?: (branchId: string) => void;
  onMergeCompleted?: (message: string) => void;
  isWhiteboardDragging?: boolean;
  onShowToast?: (message: string, type?: 'success' | 'error' | 'info') => void;
  isAnyNoteCreatingBranch?: boolean;
}

// Mermaid detection utilities
const hasMermaidBlock = (text: string): boolean => {
  return text.includes('```mermaid');
};

const extractMermaidCode = (text: string): string | null => {
  const match = text.match(/```mermaid\n([\s\S]*?)\n```/);
  return match ? match[1].trim() : null;
};

// Persona change detection utility
const isPersonaChangeMessage = (text: string): boolean => {
  return text.includes('switch to being') && text.includes('Please acknowledge this change');
};

const extractPersonaFromChangeMessage = (text: string): string | null => {
  const match = text.match(/switch to being (?:a |an )?([^.]+?)\./);
  return match ? match[1].trim() : null;
};

// Persona Change Header Component
const PersonaChangeHeader: React.FC<{ persona: string }> = ({ persona }) => (
  <div className="persona-change-header">
    <div className="persona-change-indicator">
      <span className="persona-icon">👤</span>
      <span className="persona-text">Switching to {persona}</span>
    </div>
  </div>
);

const SideChatPane: React.FC<SideChatPaneProps> = ({ isOpen, onToggle, selectedNote, onBranchCreated, onMergeCompleted, isWhiteboardDragging = false, onShowToast, isAnyNoteCreatingBranch = false }) => {
  const [chat, setChat] = useState<EnhancedMessage[]>([]);
  const [input, setInput] = useState('');
  const [loadingNotes, setLoadingNotes] = useState<Set<string>>(new Set());
  const [currentTopic, setCurrentTopic] = useState('');
  const [firstUserMessage, setFirstUserMessage] = useState(true);
  const [branchModalOpen, setBranchModalOpen] = useState(false);
  const [selectedBranchPoint, setSelectedBranchPoint] = useState<number>(-1);
  const [selectedMessageContent, setSelectedMessageContent] = useState<string>('');
  const [branchCreating, setBranchCreating] = useState(false);
  const [copyMergeModalOpen, setCopyMergeModalOpen] = useState(false);
  const [selectedCopyMergeMessageIndex, setSelectedCopyMergeMessageIndex] = useState<number>(-1);
  const [selectedCopyMergeMessageText, setSelectedCopyMergeMessageText] = useState<string>('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [clickedHeading, setClickedHeading] = useState<{ messageIndex: number; headingText: string } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Feature toggle for inline clickable headings (can be moved to settings later)
  const [inlineClickableHeadingsEnabled, setInlineClickableHeadingsEnabled] = useState(true);
  
  // Disable the old clickables logic since we now have inline clickable headings and bold text
  const [clickablesEnabled, setClickablesEnabled] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<number | null>(null);
  
  // Alert modal state
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalConfig, setAlertModalConfig] = useState({
    title: '',
    message: '',
    buttonText: 'OK',
    buttonVariant: 'primary' as 'primary' | 'danger' | 'secondary' | 'success'
  });
  
  // Track loading states for branch creation buttons
  const [loadingBranchButtons, setLoadingBranchButtons] = useState<Set<string>>(new Set());
  
  // File upload progress tracking
  const [uploadProgress, setUploadProgress] = useState<{ [fileId: string]: number }>({});
  const [filesExpanded, setFilesExpanded] = useState(false);
  const [isDragOverPane, setIsDragOverPane] = useState(false);
  
  // Animation state
  const [isAnimating, setIsAnimating] = useState(isOpen); // Start animating if initially open
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);
  
  // Web search state
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const [searchResults, setSearchResults] = useState<any>(null);
  const [searchResultsExpanded, setSearchResultsExpanded] = useState(false);
  
  // Resize functionality
  const [paneWidth, setPaneWidth] = useState(400);
  const [isResizing, setIsResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(400);
  
  // Touch/swipe functionality for mobile
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchStartY, setTouchStartY] = useState(0);
  const [isSwipeGesture, setIsSwipeGesture] = useState(false);
  

  
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const chatBodyRef = useRef<HTMLDivElement>(null);
  const resizeHandleRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { addBranchNote, getChildBranches, updateNote, undoMerge, state: { canvasId } } = useWhiteboardStore();
  const { user } = useAuth();
  const { canCreateThreadIdea, canCreateTotalNote, canPerformWebSearch, canUploadFile } = useFeatureAccess();
  const { performWithQuotaCheck, incrementQuotaUsage } = useQuotaEnforcement();


  // Helper function to show alert modal
  const showAlert = (title: string, message: string, buttonVariant: 'primary' | 'danger' | 'secondary' | 'success' = 'primary') => {
    setAlertModalConfig({
      title,
      message,
      buttonText: 'OK',
      buttonVariant
    });
    setShowAlertModal(true);
  };

  // Calculate max width (40% of screen width)
  const maxWidth = Math.min(window.innerWidth * 0.4, 800);
  const minWidth = 300;

  // Handle resize start
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setStartX(e.clientX);
    setStartWidth(paneWidth);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // Handle resize move (optimized with requestAnimationFrame)
  useEffect(() => {
    let rafId: number;

    const handleResizeMove = (e: MouseEvent) => {
      if (!isResizing) return;
      
      e.preventDefault();
      
      // Use requestAnimationFrame for smoother updates
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      
      rafId = requestAnimationFrame(() => {
        const deltaX = startX - e.clientX;
        const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + deltaX));
        setPaneWidth(newWidth);
      });
    };

    const handleResizeEnd = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove, { passive: false });
      document.addEventListener('mouseup', handleResizeEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isResizing, startX, startWidth, maxWidth, minWidth]);

  // Update max width when window resizes (debounced for performance)
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const handleWindowResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const newMaxWidth = Math.min(window.innerWidth * 0.4, 800);
        if (paneWidth > newMaxWidth) {
          setPaneWidth(newMaxWidth);
        }
      }, 250); // Increased debounce time for better performance
    };

    window.addEventListener('resize', handleWindowResize, { passive: true });
    return () => {
      window.removeEventListener('resize', handleWindowResize);
      clearTimeout(timeoutId);
    };
  }, [paneWidth]);

  // Touch event handlers for swipe-to-close functionality
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setTouchStartX(touch.clientX);
    setTouchStartY(touch.clientY);
    setIsSwipeGesture(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartX || !touchStartY) return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartX;
    const deltaY = touch.clientY - touchStartY;
    
    // Check if this is a horizontal swipe (more horizontal than vertical movement)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      setIsSwipeGesture(true);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartX || !touchStartY || !isSwipeGesture) {
      setTouchStartX(0);
      setTouchStartY(0);
      setIsSwipeGesture(false);
      return;
    }
    
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartX;
    const deltaY = touch.clientY - touchStartY;
    
    // Check if it's a right swipe (positive deltaX) with sufficient distance
    if (deltaX > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
      // Close the chat pane
      onToggle();
    }
    
    // Reset touch state
    setTouchStartX(0);
    setTouchStartY(0);
    setIsSwipeGesture(false);
  };

  // Update chat when selected note changes (memoized for performance)
  useEffect(() => {
    if (selectedNote) {
      const messages = (selectedNote.chatHistory as Message[]) || [];
      
      //console.log('Setting chat from selected note:', messages);
      setChat(messages as EnhancedMessage[]);
      setCurrentTopic(selectedNote.topic || 'New Topic');
      // Consider it first message if firstMessageSent is not true
      setFirstUserMessage(!selectedNote.firstMessageSent);
      
      // Files section always starts collapsed - no auto-expand
      setFilesExpanded(false);
    } else {
      setChat([]);
      setCurrentTopic('');
      setFilesExpanded(false);
    }
  }, [selectedNote?.id, selectedNote?.chatHistory, selectedNote?.topic, selectedNote?.firstMessageSent]);

  // Remove the problematic useEffect that was causing infinite loops
  // We'll update the note directly in the chat handlers instead

  // Auto-scroll to bottom when new messages arrive (optimized)
  useEffect(() => {
    if (chatBodyRef.current) {
      // Use requestAnimationFrame for smoother scrolling
      requestAnimationFrame(() => {
        if (chatBodyRef.current) {
          chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
      });
    }
  }, [chat.length, loadingNotes.size]); // Only trigger on actual changes

  useEffect(() => {
    const isCurrentNoteLoading = selectedNote ? loadingNotes.has(selectedNote.id) : false;
    if (!isCurrentNoteLoading && isOpen && selectedNote) {
      // Add a small delay to ensure the textarea is rendered and ready
      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
      
      return () => clearTimeout(timer);
    }
  }, [loadingNotes, isOpen, selectedNote]);

  // Prevent canvas zoom when scrolling chat
  useEffect(() => {
    const chatBody = chatBodyRef.current;
    if (!chatBody) return;
    
    const handleWheel = (e: WheelEvent) => {
      e.stopPropagation();
      
      const atTop = chatBody.scrollTop === 0;
      const atBottom = chatBody.scrollTop + chatBody.clientHeight >= chatBody.scrollHeight - 1;
      
      if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) {
        e.preventDefault();
      }
    };
    
    chatBody.addEventListener('wheel', handleWheel, { passive: false });
    return () => chatBody.removeEventListener('wheel', handleWheel);
  }, []);

  // Set initial focus state when component mounts
  useEffect(() => {
    if (inputRef.current) {
      setIsInputFocused(true);
    }
  }, []);

  // Handle slide animation
  useEffect(() => {
    if (isOpen) {
      // Opening: render immediately, start from closed position
      setIsClosing(false);
      setShouldRender(true);
      setIsAnimating(true); // Start with closed class
      
      // Small delay to ensure DOM is ready, then slide in
      const timer = setTimeout(() => {
        setIsAnimating(false); // Remove closed class to slide in
      }, 10);
      
      return () => clearTimeout(timer);
    } else {
      // Closing: start closing animation, then remove from DOM
      setIsClosing(true);
      setIsAnimating(true);
      
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsAnimating(false);
        setIsClosing(false);
      }, 300); // Match CSS transition duration
      
      return () => clearTimeout(timer);
    }
  }, [isOpen]);



  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    
    // Enforce character limit from environment variable
    if (newValue.length <= CHARACTER_LIMIT) {
      setInput(newValue);
    }
    
    // Auto-resize the textarea
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      const newHeight = Math.min(inputRef.current.scrollHeight, 120);
      inputRef.current.style.height = newHeight + 'px';
    }
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    // Delay the blur handling to allow click events to process first
    setTimeout(() => {
      setIsInputFocused(false);
      // Reset height when losing focus
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
      }
    }, 0);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleBranchFromMessage = (messageIndex: number) => {
    if (messageIndex <= 0 || !selectedNote) return;
    const messageContent = chat[messageIndex]?.text || '';
    setSelectedBranchPoint(messageIndex);
    setSelectedMessageContent(messageContent);
    setBranchModalOpen(true);
  };

  const handleCopyMergeFromMessage = (messageIndex: number) => {
    if (!selectedNote) return;
    const messageContent = chat[messageIndex]?.text || '';
    setSelectedCopyMergeMessageIndex(messageIndex);
    setSelectedCopyMergeMessageText(messageContent);
    setCopyMergeModalOpen(true);
  };

  const handleCopyMerge = async (targetNoteId: string, editedMessage: string) => {
    if (!selectedNote || selectedCopyMergeMessageIndex === -1) return;
    
    try {
      const { copyMergeMessage } = useWhiteboardStore.getState();
      copyMergeMessage(selectedNote.id, selectedCopyMergeMessageIndex, targetNoteId, editedMessage);
      
      setCopyMergeModalOpen(false);
      setSelectedCopyMergeMessageIndex(-1);
      setSelectedCopyMergeMessageText('');
    } catch (error) {
      console.error('Error copying message:', error);
      throw error;
    }
  };

  const handleCreateBranch = async (color: string, inheritanceType: InheritanceType) => {
    if (!selectedNote || selectedBranchPoint <= 0) return;
    
    console.log(`🌿 [SideChatPane] handleCreateBranch called (Modal)`, {
      parentNoteId: selectedNote.id,
      color,
      inheritanceType,
      branchPoint: selectedBranchPoint,
      userId: user?.uid,
      canvasId,
      hasUser: !!user,
      hasCanvasId: !!canvasId
    });
    
    setBranchCreating(true);
    
    // Start the pulsating animation immediately
    console.log('🎬 [SideChatPane] Starting pulsating animation for note:', selectedNote.id);
    updateNote(selectedNote.id, { isCreatingBranch: true });
    
    // Show toast notification for mobile users (no cursor on mobile)
    const isMobile = window.innerWidth < 768;
    if (isMobile && onShowToast) {
      onShowToast('Branch creation in progress...', 'info');
    }
    
    try {
      // Check quota if user is authenticated
      if (user) {
        const canCreateIdea = await canCreateThreadIdea();
        
        if (!canCreateIdea) {
          showAlert(
            'Total Notes Limit Reached',
            'You\'ve reached your total notes limit. Please upgrade your plan to create more notes.',
            'danger'
          );
          setBranchCreating(false);
          // Clear the pulsating animation
          console.log('🎬 [SideChatPane] Clearing pulsating animation (quota exceeded - total notes)');
          updateNote(selectedNote.id, { isCreatingBranch: false });
          return;
        }

        const canCreateTotal = await canCreateTotalNote();
        
        if (!canCreateTotal) {
          showAlert(
            'Canvas Note Limit Reached',
            'You\'ve reached your total notes limit for this canvas (20 notes). Please upgrade your plan or create a new canvas.',
            'danger'
          );
          setBranchCreating(false);
          // Clear the pulsating animation
          console.log('🎬 [SideChatPane] Clearing pulsating animation (quota exceeded - canvas notes)');
          updateNote(selectedNote.id, { isCreatingBranch: false });
          return;
        }
      }

      // Update the parent note's chat history first to ensure it's in sync
      updateNote(selectedNote.id, { 
        chatHistory: chat as Array<{ sender: 'user' | 'ai'; text: string }> 
      });
      
      // Wait for the branch creation to complete (including topic generation)
      await addBranchNote(selectedNote.id, color, selectedBranchPoint, inheritanceType, undefined, onBranchCreated);
      
      // Increment total threads quota
      if (user) {
        const incrementResult = await incrementQuotaUsage('totalThreads');
        if (!incrementResult.success) {
          console.warn('Total threads quota increment failed:', incrementResult.error);
        }
      }
      
      setBranchModalOpen(false);
      setSelectedBranchPoint(-1);
    
    } catch (error) {
      console.error('❌ [SideChatPane] Error creating branch (Modal):', error);
      // Clear the pulsating animation on error
      console.log('🎬 [SideChatPane] Clearing pulsating animation (error occurred)');
      updateNote(selectedNote.id, { isCreatingBranch: false });
    } finally {
      setBranchCreating(false);
    }
  };

  const getExistingBranchesForMessage = (messageIndex: number): number => {
    if (!selectedNote) return 0;
    const branches = getChildBranches(selectedNote.id);
    return branches.filter(branch => branch.branchPoint === messageIndex).length;
  };



  const handleInputKeyDown = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!selectedNote) return;
    const isCurrentNoteLoading = loadingNotes.has(selectedNote.id);
    if (e.key === 'Enter' && !e.shiftKey && input.trim() && !isCurrentNoteLoading) {
      e.preventDefault(); // Only prevent default when sending message
      
      const newChat = [...chat, { sender: 'user' as 'user', text: input.trim() }];

      // Use quota enforcement for message sending
      const result = await performWithQuotaCheck('monthlyChatMessages', async () => {
        // Track chat message sent
        trackEvent('chat_message_sent', { 
          message_length: input.trim().length,
          includes_code_block: input.trim().includes('```')
        });

        setChat(newChat);
        setInput('');
        setLoadingNotes(prev => new Set(Array.from(prev).concat(selectedNote.id)));
        
        // Update the note with the new chat
        if (selectedNote) {
          updateNote(selectedNote.id, { 
            chatHistory: newChat as Array<{ sender: 'user' | 'ai'; text: string }>
          });
        }
      });

      if (!result.success) {
        showAlert(
          'Monthly Message Limit Reached',
          result.error?.message || 'You have reached your monthly chat message limit. Upgrade your plan to continue chatting.',
          'danger'
        );
        return;
      }
      
      try {
        const messagesForBackend = newChat.map(m => ({
          role: m.sender === 'user' ? 'user' : 'assistant',
          content: m.text,
        }));
        
        // Extract file IDs from attached files
        const fileIds = selectedNote?.attachedFiles
          ?.filter(file => file.uploadStatus === 'completed')
          ?.map(file => file.fileId || file.name) // Use fileId for backend, fallback to name for backward compatibility
          ?.filter(Boolean) || [];
        
        // For branch notes, always use /system_design endpoint
        // For root notes, use /first_message only for the actual first message
        const isBranchNote = selectedNote?.parentId !== undefined;
        const shouldUseFirstMessage = !isBranchNote && firstUserMessage;
        
        // Check web search quota if web search is enabled
        if (webSearchEnabled && user && canvasId) {
          try {
            const canSearch = await canPerformWebSearch();
            if (!canSearch) {
              showAlert(
                'Web Search Limit Reached',
                'You\'ve reached your web search limit. Please upgrade your plan to perform more web searches.',
                'danger'
              );
              setLoadingNotes(prev => new Set(Array.from(prev).filter(id => id !== selectedNote.id)));
              return;
            }
          } catch (error) {
            console.error('Error checking web search quota:', error);
            // Fallback: allow web search even if quota check fails
          }
        }
        
        let response;
        const requestBody = {
          messages: messagesForBackend,
          ...((selectedNote?.systemPrompt || selectedNote?.inheritedSystemPrompt) && { 
            systemPrompt: selectedNote.systemPrompt || selectedNote.inheritedSystemPrompt 
          }),
          ...(fileIds.length > 0 && { fileIds }),
          ...(webSearchEnabled && { webSearch: { enabled: true, query: input.trim() } })
        };
        
        if (shouldUseFirstMessage) {
          // For root notes, use the /first_message endpoint only for the actual first message
          response = await authenticatedApiPost('FIRST_MESSAGE', requestBody);
        } else {
          // For branch notes or subsequent messages, use /system_design endpoint
          response = await authenticatedApiPost('SYSTEM_DESIGN', requestBody);
        }
        
        const data = await response.json();
        
        // Handle web search results if available
        if (data.webSearchResults) {
          setSearchResults(data.webSearchResults);
          setSearchResultsExpanded(false); // Minimize by default for new search results
          
          // Increment web search quota
          if (user && canvasId) {
            const incrementResult = await incrementQuotaUsage('webSearches');
            if (!incrementResult.success) {
              console.warn('Web search quota increment failed:', incrementResult.error);
            }
          }
        }
        
        // Reset web search checkbox after sending
        setWebSearchEnabled(false);
        
        if (shouldUseFirstMessage) {
          // Handle first message response from /first_message endpoint (root notes only)
          const responseMessage: EnhancedMessage = {
            sender: 'ai',
            text: data.message || 'I\'m here to help!',
            topic: data.topic,
            suggestions: data.suggestions || [],
            thought_thread: data.thought_thread || undefined,
          };
          
          const finalChat = [...newChat, responseMessage];

          setChat(finalChat);
          
          // Update the note with the final chat and mark first message as sent
          if (selectedNote) {
            updateNote(selectedNote.id, { 
              chatHistory: finalChat as Array<{ sender: 'user' | 'ai'; text: string }>,
              firstMessageSent: true
            });
          }
          
          // Quota increment is now handled by the quota enforcement hook
          
          // Update topic if provided (only for root notes)
          if (data.topic && data.topic !== currentTopic) {
            setCurrentTopic(data.topic);
            if (selectedNote && !selectedNote.hasUserSetTopic) {
              updateNote(selectedNote.id, { topic: data.topic });
            }
          }
          
          setFirstUserMessage(false);
        } else {
          // Handle responses from /system_design endpoint (branch notes and subsequent messages)
          if (data.message) {
            const aiMessage: EnhancedMessage = {
              sender: 'ai',
              text: data.message,
              suggestions: data.suggestions || [],
              analysis: data.analysis,
              topic: data.topic,
              thought_thread: data.thought_thread || undefined,
            };
            
            const finalChat = [...newChat, aiMessage];

            setChat(finalChat);
            
            // Update the note with the final chat
            if (selectedNote) {
              const updates: any = { 
                chatHistory: finalChat as Array<{ sender: 'user' | 'ai'; text: string }>
              };
              
              // Mark first message as sent if this is the first message for this note
              if (firstUserMessage) {
                updates.firstMessageSent = true;
                setFirstUserMessage(false);
              }
              
              updateNote(selectedNote.id, updates);
            }
            
            // Update topic if provided (but don't overwrite branch topics)
            if (data.topic && data.topic !== currentTopic) {
              setCurrentTopic(data.topic);
              // Only update topic for root notes or if user hasn't set a custom topic
              if (selectedNote && !selectedNote.hasUserSetTopic && !selectedNote.parentId) {
                updateNote(selectedNote.id, { topic: data.topic });
              }
            }
          } else {
            console.error('No message in response:', data);
          }
        }
      } catch (error: any) {
        console.error('Error sending message:', error);
        
        // Check if this is a "message too long" error from the backend
        let errorText = 'Sorry, I encountered an error. Please try again.';
        if (error.message && error.message.includes('Message too long')) {
          errorText = `Your message is too long. Please keep it under ${CHARACTER_LIMIT} characters.`;
        }
        
        const errorMessage: EnhancedMessage = {
          sender: 'ai',
          text: errorText,
          thought_thread: undefined,
        };
        const finalChat = [...newChat, errorMessage];

        setChat(finalChat);
        
        // Update the note with the error message
        if (selectedNote) {
          updateNote(selectedNote.id, { 
            chatHistory: finalChat as Array<{ sender: 'user' | 'ai'; text: string }>
          });
        }
      } finally {
        setLoadingNotes(prev => {
          const newSet = new Set(Array.from(prev));
          newSet.delete(selectedNote.id);
          return newSet;
        });
      }
    }
  };

  const handleTopicChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTopic = e.target.value;
    setCurrentTopic(newTopic);
    // Update the note with the new topic
    if (selectedNote) {
      const isMeaningfulTopic = Boolean(newTopic.trim()) && newTopic !== 'New Topic';
      updateNote(selectedNote.id, { 
        topic: newTopic,
        hasUserSetTopic: isMeaningfulTopic
      });
    }
  };

  const handleQuickBranch = async (topic: string, messageIndex: number) => {
    if (!selectedNote) {
      return;
    }
    
    // Create unique button ID for loading state
    const buttonId = `${messageIndex}-${topic}`;
    
    // Set loading state for this specific button
    setLoadingBranchButtons(prev => new Set([...Array.from(prev), buttonId]));
    
    // Set the clicked heading for visual feedback
    setClickedHeading({ messageIndex, headingText: topic });
    
    // Start the pulsating animation immediately by setting isCreatingBranch
    console.log('🎬 [SideChatPane] Starting pulsating animation for quick branch on note:', selectedNote.id);
    updateNote(selectedNote.id, { isCreatingBranch: true });
    
    // Show toast notification for mobile users (no cursor on mobile)
    const isMobile = window.innerWidth < 768;
    if (isMobile && onShowToast) {
      onShowToast('Branch creation in progress...', 'info');
    }
    
    // Use centralized glassmorphic color palette and pick a different color from parent
    let glassmorphicColor = getRandomGlassmorphicColorExcluding(selectedNote.color);
    // Use the specific message index as the branch point
    const branchPoint = messageIndex;

    // Create a custom callback that clears the clicked heading when branch is created
    const customOnBranchCreated = (branchId: string) => {
      setClickedHeading(null);
      
      // Programmatically focus textarea when branch creation completes
      if (inputRef.current) {
        inputRef.current.focus();
      }
      
      if (onBranchCreated) {
        onBranchCreated(branchId);
      }
    };
    
    console.log(`🌿 [SideChatPane] handleQuickBranch called (Inline)`, {
      parentNoteId: selectedNote.id,
      topic,
      messageIndex,
      userId: user?.uid,
      canvasId,
      hasUser: !!user,
      hasCanvasId: !!canvasId
    });

    // Check quota if user is authenticated
    if (user) {
      try {
        const canCreateIdea = await canCreateThreadIdea();
        
        if (!canCreateIdea) {
          showAlert(
            'Total Notes Limit Reached',
            'You\'ve reached your total notes limit. Please upgrade your plan to create more notes.',
            'danger'
          );
          setClickedHeading(null);
          setLoadingBranchButtons(prev => {
            const newSet = new Set(prev);
            newSet.delete(buttonId);
            return newSet;
          });
          // Clear the pulsating animation
          console.log('🎬 [SideChatPane] Clearing pulsating animation (quota exceeded - total notes)');
          updateNote(selectedNote.id, { isCreatingBranch: false });
          return;
        }

        const canCreateTotal = await canCreateTotalNote();
        
        if (!canCreateTotal) {
          showAlert(
            'Canvas Note Limit Reached',
            'You\'ve reached your total notes limit for this canvas (20 notes). Please upgrade your plan or create a new canvas.',
            'danger'
          );
          setClickedHeading(null);
          setLoadingBranchButtons(prev => {
            const newSet = new Set(prev);
            newSet.delete(buttonId);
            return newSet;
          });
          // Clear the pulsating animation
          console.log('🎬 [SideChatPane] Clearing pulsating animation (quota exceeded - canvas notes)');
          updateNote(selectedNote.id, { isCreatingBranch: false });
          return;
        }
      } catch (error) {
        console.error('❌ [SideChatPane] Error checking quota for quick branch creation (Inline):', error);
        // Clear the pulsating animation on error
        console.log('🎬 [SideChatPane] Clearing pulsating animation (quota check error)');
        updateNote(selectedNote.id, { isCreatingBranch: false });
        // Fallback: allow branch creation even if quota check fails
      }
    }

    // Try to create the branch and handle the result
    const result = await addBranchNote(selectedNote.id, glassmorphicColor.solid, branchPoint, 'summary', topic, customOnBranchCreated);

    // Increment total threads quota if branch creation was successful
    if (result === BranchCreationResult.SUCCESS && user) {
      const incrementResult = await incrementQuotaUsage('totalThreads');
      if (!incrementResult.success) {
        console.warn('Total threads quota increment failed:', incrementResult.error);
      }
    }

    // Handle different error cases
    if (result !== BranchCreationResult.SUCCESS) {
      // Clear the clicked heading immediately since it failed
      setClickedHeading(null);
      
      // Clear the pulsating animation on error
      console.log('🎬 [SideChatPane] Clearing pulsating animation (branch creation failed)');
      updateNote(selectedNote.id, { isCreatingBranch: false });
      
      // Show appropriate error message
      if (onShowToast) {
        switch (result) {
          case BranchCreationResult.CANNOT_BRANCH_FROM_FIRST_MESSAGE:
            onShowToast('Unable to create branch from first message', 'error');
            break;
          case BranchCreationResult.PARENT_NOT_FOUND:
            onShowToast('Parent note not found', 'error');
            break;
          case BranchCreationResult.BRANCH_POINT_OUT_OF_RANGE:
            onShowToast('Invalid branch point', 'error');
            break;
          case BranchCreationResult.MAX_DEPTH_EXCEEDED:
            onShowToast('Maximum branch depth exceeded', 'error');
            break;
          case BranchCreationResult.TOO_MANY_BRANCHES:
            const branchLimit = parseInt(process.env.REACT_APP_BRANCH_LIMIT_PER_NOTE || '5', 10);
            onShowToast(`Maximum ${branchLimit} branches allowed per note`, 'error');
            break;
          default:
            onShowToast('Failed to create branch', 'error');
        }
      }
    }
    
    // Always clear loading state at the end
    setLoadingBranchButtons(prev => {
      const newSet = new Set(prev);
      newSet.delete(buttonId);
      return newSet;
    });
  };

  const handleDeleteMessage = (messageIndex: number) => {
    setMessageToDelete(messageIndex);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDeleteMessage = () => {
    if (messageToDelete !== null && selectedNote) {
      // Create new chat array without the deleted message
      const newChat = chat.filter((_, index) => index !== messageToDelete);
      
      // Update the note's chat history
      updateNote(selectedNote.id, { 
        chatHistory: newChat as Array<{ sender: 'user' | 'ai'; text: string }>
      });
      
      // Update local chat state
      setChat(newChat);
    }
    setShowDeleteConfirm(false);
    setMessageToDelete(null);
  };

  const handleUndoMerge = (messageIndex: number) => {
    if (!selectedNote) return;
    
    // Call the store function to undo the merge
    undoMerge(messageIndex, selectedNote.id);
    
    // Show toast notification via callback
    if (onMergeCompleted) {
      onMergeCompleted('Merge undone successfully');
    }
  };

  const handleCancelDeleteMessage = () => {
    setShowDeleteConfirm(false);
    setMessageToDelete(null);
  };

  // File upload button handler
  const handleFileUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      handlePaneFileUpload(files);
    }
    // Reset the input so the same file can be selected again
    e.target.value = '';
  };



  const handleRemoveFile = (fileId: string) => {
    if (!selectedNote) return;
    
    const updatedFiles = (selectedNote.attachedFiles || []).filter(file => file.id !== fileId);
    updateNote(selectedNote.id, {
      attachedFiles: updatedFiles.length > 0 ? updatedFiles : undefined
    });
  };

  const toggleFilesExpanded = () => {
    setFilesExpanded(!filesExpanded);
  };

  // Drag & drop handlers for SideChatPane
  const handlePaneDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOverPane(true);
  };

  const handlePaneDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set drag over to false if we're leaving the pane itself
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOverPane(false);
    }
  };

  const handlePaneDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOverPane(false);
    
    if (!selectedNote) return;
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handlePaneFileUpload(files);
    }
  };

  const handlePaneFileUpload = async (files: File[]) => {

    if (!selectedNote) return;
    
    const currentFiles = selectedNote.attachedFiles || [];
    
    // Check quota if user is authenticated and canvas ID is available
    if (user && canvasId) {
      try {
        const canUpload = await canUploadFile();
        if (!canUpload) {
          showAlert(
            'File Attachment Limit Reached',
            'You\'ve reached your file attachment limit. Please upgrade your plan to upload more files.',
            'danger'
          );
          return;
        }
      } catch (error) {
        console.error('Error checking file upload quota:', error);
        // Fallback: allow upload even if quota check fails
      }
    }
    
    // Check file limits
    if (currentFiles.length + files.length > 3) {
      showAlert(
        'File Limit Exceeded',
        'Maximum 3 files allowed per note',
        'danger'
      );
      return;
    }
    
    try {
      // Create temporary file attachments for progress tracking
      const tempFileAttachments = files.map(file => ({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        size: file.size,
        type: file.type,
        uploadDate: new Date(),
        uploadStatus: 'uploading' as const,
        file: file // Store the original file for upload
      }));
      
      // Add temporary files to the note immediately for progress display
      updateNote(selectedNote.id, {
        attachedFiles: [...currentFiles, ...tempFileAttachments]
      });
      
      // Auto-expand files panel to show upload progress
      setFilesExpanded(true);
      
      // Upload each file using our real upload API
      const uploadPromises = tempFileAttachments.map(async (tempFile) => {

        
        try {
          const uploadedFile = await uploadFile(tempFile.file, (progress) => {

            // Update progress state
            setUploadProgress(prev => ({
              ...prev,
              [tempFile.id]: progress
            }));
          });
          

          
          // Convert to FileAttachment format for the note (replacing temp file)
          const fileAttachment = {
            id: tempFile.id, // Keep the same ID as temp file
            name: uploadedFile.originalName, // Use original filename for display
            fileId: uploadedFile.fileName, // Store backend file ID for API calls
            size: uploadedFile.size,
            type: uploadedFile.fileType,
            uploadDate: new Date(),
            uploadStatus: 'completed' as const,
            url: uploadedFile.downloadUrl
          };
          
          // Clear progress for this file
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[tempFile.id];
            return newProgress;
          });
          
          return fileAttachment;
        } catch (error) {
          console.error('❌ SideChatPane: Upload failed for', tempFile.name, error);
          
          // Create failed file attachment
          const failedAttachment = {
            id: tempFile.id, // Use the same ID as the temp file
            name: tempFile.name,
            size: tempFile.size,
            type: tempFile.type,
            uploadDate: new Date(),
            uploadStatus: 'failed' as const,
            error: error instanceof Error ? error.message : 'Upload failed'
          };
          
          return failedAttachment;
        }
      });
      
      // Wait for all uploads to complete
      const newFileAttachments = await Promise.all(uploadPromises);
      
      // Update note with final file attachments (replacing temp files)
      updateNote(selectedNote.id, {
        attachedFiles: [...currentFiles, ...newFileAttachments]
      });
      
      // Increment file attachments quota
      if (user && canvasId) {
        const incrementResult = await incrementQuotaUsage('fileAttachments');
        if (!incrementResult.success) {
          console.warn('File attachments quota increment failed:', incrementResult.error);
        }
      }
      
      // Files section stays collapsed even after upload - user must manually expand
      
      // Show success/failure summary
      const successful = newFileAttachments.filter(f => f.uploadStatus === 'completed').length;
      const failed = newFileAttachments.filter(f => f.uploadStatus === 'failed').length;
      
      if (successful > 0 && failed === 0) {

      } else if (successful > 0 && failed > 0) {
        showAlert(
          'Partial Upload Success',
          `${successful} files uploaded successfully, ${failed} failed. Check console for details.`,
          'secondary'
        );
      } else {
        showAlert(
          'Upload Failed',
          'All file uploads failed. Please try again.',
          'danger'
        );
      }
      
    } catch (error) {
      console.error('💥 SideChatPane: Drag & drop upload process failed:', error);
      showAlert(
        'Upload Failed',
        'File upload failed. Please try again.',
        'danger'
      );
    }
  };

  if (!shouldRender) {
    return null;
  }

  const isAtMaxWidth = paneWidth >= maxWidth;

  return (
    <>
      {/* Backdrop for tap-to-close functionality */}
      {isOpen && (
        <div 
          className="side-chat-backdrop"
          onClick={onToggle}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            zIndex: 999,
            display: window.innerWidth <= 768 ? 'block' : 'none' // Only show on mobile
          }}
        />
      )}
      
      <div 
        className={`side-chat-pane ${isResizing ? 'resizing' : ''} ${isAtMaxWidth ? 'at-max-width' : ''} ${isDragOverPane ? 'drag-over-pane' : ''} ${isWhiteboardDragging ? 'performance-mode' : ''} ${(isClosing || (isOpen && isAnimating)) ? 'closed' : ''} ${(isAnimating || isClosing) ? 'animating' : ''} ${isAnyNoteCreatingBranch ? 'branch-creating' : ''}`}
        style={{ width: `${paneWidth}px` }}
        onDragOver={handlePaneDragOver}
        onDragLeave={handlePaneDragLeave}
        onDrop={handlePaneDrop}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
      {/* Resize Handle */}
      <div
        ref={resizeHandleRef}
        className="resize-handle"
        onMouseDown={handleResizeStart}
        title={`Drag to resize (${paneWidth}px / ${Math.round(maxWidth)}px max)`}
      />
      
      {/* Drag Over Indicator */}
      {isDragOverPane && (
        <div className="drag-over-indicator">
          <div className="drag-over-content">
            <span className="drag-over-icon">📄</span>
            <span className="drag-over-text">Drop images and PDF files here to upload</span>
          </div>
        </div>
      )}
      
      <div className="side-chat-header">
        <div className="side-chat-title">
          <input
            type="text"
            value={currentTopic}
            onChange={handleTopicChange}
            placeholder="Note Topic"
            className="topic-input"
          />
          
          {/* Persona Indicator under the topic */}
          {selectedNote && (
            <div 
              className="persona-header-indicator" 
              onClick={() => {
                // Open the persona switch dialog by triggering the persona button click
                const personaButton = document.querySelector(`[data-note-id="${selectedNote.id}"] .persona-btn`) as HTMLButtonElement;
                if (personaButton) {
                  personaButton.click();
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 0',
                marginTop: '4px',
                fontSize: '11px',
                color: selectedNote.hasCustomSystemPrompt || selectedNote.inheritedSystemPrompt
                  ? '#1976d2'
                  : '#ff9800',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                width: 'fit-content',
                maxWidth: '100%'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
              title={selectedNote.hasCustomSystemPrompt || selectedNote.inheritedSystemPrompt
                ? `Click to change persona (currently: ${selectedNote.persona || selectedNote.inheritedSystemPrompt})`
                : 'Click to set AI persona'
              }
            >
              <span style={{ fontSize: '11px' }}>
                {selectedNote.hasCustomSystemPrompt || selectedNote.inheritedSystemPrompt ? '👤' : '⚡'}
              </span>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {selectedNote.hasCustomSystemPrompt || selectedNote.inheritedSystemPrompt
                  ? selectedNote.persona || selectedNote.inheritedSystemPrompt
                  : 'Set AI persona'
                }
              </span>
              <span style={{
                fontSize: '9px',
                opacity: 0.7,
                fontStyle: 'italic',
                whiteSpace: 'nowrap'
              }}>
                {!(selectedNote.hasCustomSystemPrompt || selectedNote.inheritedSystemPrompt) ? 'Click' : 'Change'}
              </span>
            </div>
          )}
        </div>
        
        <button 
          className="close-button"
          onClick={onToggle}
          title="Close Chat Pane (Ctrl+Shift+C)"
          style={{
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '8px',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: '16px',
            color: '#666',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
            e.currentTarget.style.color = '#333';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.7)';
            e.currentTarget.style.color = '#666';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          ✕
        </button>
      </div>

      <div 
        className="side-chat-body" 
        ref={chatBodyRef}
      >
        {chat.map((message, index) => {
          // Check if this message is a merge summary
          const mergeInfo = selectedNote?.mergeMessages?.find(m => m.messageIndex === index);
          const isMergeMessage = !!mergeInfo;
          
          // Check if this message is a copied message
          const copyInfo = selectedNote?.copyMessages?.find(m => m.messageIndex === index);
          const isCopyMessage = !!copyInfo;
          
          // Check if this is a persona change message
          const isPersonaChange = isPersonaChangeMessage(message.text);
          const personaFromChange = isPersonaChange ? extractPersonaFromChangeMessage(message.text) : null;
          
          const listSummary = message.sender === 'ai' && !isMergeMessage && !isCopyMessage ? extractMarkdownListSummary(message.text) : { headers: [], listItems: [] };

          return (
            <React.Fragment key={index}>
              {/* Persona Change Header - Outside message container for proper left alignment */}
              {isPersonaChange && personaFromChange && (
                <PersonaChangeHeader key={`persona-${index}`} persona={personaFromChange} />
              )}
              
              <div 
                className={`message ${message.sender} ${isMergeMessage ? 'merge-message' : ''} ${isCopyMessage ? 'copy-message' : ''}`}
                onMouseEnter={(e) => {
                  const deleteBtn = e.currentTarget.querySelector('.message-delete-btn') as HTMLButtonElement;
                  if (deleteBtn) {
                    deleteBtn.style.opacity = '1';
                    deleteBtn.style.backgroundColor = 'rgba(255, 255, 255, 1)';
                    deleteBtn.style.color = '#f44336';
                    deleteBtn.style.transform = 'scale(1.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  const deleteBtn = e.currentTarget.querySelector('.message-delete-btn') as HTMLButtonElement;
                  if (deleteBtn) {
                    deleteBtn.style.opacity = '0';
                    deleteBtn.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                    deleteBtn.style.color = '#666';
                    deleteBtn.style.transform = 'scale(1)';
                  }
                }}
              >
                <div className="message-content">
                {/* Delete button - hover to reveal */}
                <button
                  className="message-delete-btn"
                  onClick={() => handleDeleteMessage(index)}
                  title="Delete message"
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: '#666',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    transition: 'all 0.2s ease',
                    opacity: 0,
                    zIndex: 10
                  }}
                >
                  ✕
                </button>
                <div className="message-text">
                  {isMergeMessage && (
                    <div className="merge-message-container">
                      <span className="merge-prefix">🔄 From "{mergeInfo.sourceBranchName}": </span>
                      <button 
                        className="undo-merge-btn"
                        onClick={() => handleUndoMerge(index)}
                        title="Undo this merge"
                      >
                        ↩️ Undo
                      </button>
                    </div>
                  )}
                  {isCopyMessage && (
                    <span className="copy-prefix">📋 Copied from "{copyInfo.sourceNoteName}": </span>
                  )}
                  {message.sender === 'ai' ? (
                    <>
                      {/* Display AI reasoning thoughts if available */}
                      {message.thought_thread && message.thought_thread.length > 0 && (
                        <ThoughtsDisplay thoughts={message.thought_thread} />
                      )}
                      <ReactMarkdown
                        rehypePlugins={[rehypeSanitize]}
                        remarkPlugins={[remarkGfm]}
                        components={inlineClickableHeadingsEnabled ? {
                          h1: ({ node, ...props }) => {
                            const headingText = extractTextFromChildren(props.children).trim();
                            if (!headingText) return <h1 {...props} />;
                            return (
                              <div 
                                className="markdown-element-with-indicator"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  handleQuickBranch(headingText, index);
                                }}
                                title={`Create branch: ${headingText}`}
                              >
                                <h1 {...props} />
                              </div>
                            );
                          },
                          h2: ({ node, ...props }) => {
                            const headingText = extractTextFromChildren(props.children).trim();
                            if (!headingText) return <h2 {...props} />;
                            return (
                              <div 
                                className="markdown-element-with-indicator"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  handleQuickBranch(headingText, index);
                                }}
                                title={`Create branch: ${headingText}`}
                              >
                                <h2 {...props} />
                              </div>
                            );
                          },
                          h3: ({ node, ...props }) => {
                            const headingText = extractTextFromChildren(props.children).trim();
                            if (!headingText) return <h3 {...props} />;
                            return (
                              <div 
                                className="markdown-element-with-indicator"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  handleQuickBranch(headingText, index);
                                }}
                                title={`Create branch: ${headingText}`}
                              >
                                <h3 {...props} />
                              </div>
                            );
                          },
                          h4: ({ node, ...props }) => {
                            const headingText = extractTextFromChildren(props.children).trim();
                            if (!headingText) return <h4 {...props} />;
                            return (
                              <div 
                                className="markdown-element-with-indicator"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  handleQuickBranch(headingText, index);
                                }}
                                title={`Create branch: ${headingText}`}
                              >
                                <h4 {...props} />
                              </div>
                            );
                          },
                          h5: ({ node, ...props }) => {
                            const headingText = extractTextFromChildren(props.children).trim();
                            if (!headingText) return <h5 {...props} />;
                            return (
                              <div 
                                className="markdown-element-with-indicator"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  handleQuickBranch(headingText, index);
                                }}
                                title={`Create branch: ${headingText}`}
                              >
                                <h5 {...props} />
                              </div>
                            );
                          },
                          h6: ({ node, ...props }) => {
                            const headingText = extractTextFromChildren(props.children).trim();
                            if (!headingText) return <h6 {...props} />;
                            return (
                              <div 
                                className="markdown-element-with-indicator"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  handleQuickBranch(headingText, index);
                                }}
                                title={`Create branch: ${headingText}`}
                              >
                                <h6 {...props} />
                              </div>
                            );
                          },
                          strong: ({ node, ...props }) => {
                            const boldText = extractTextFromChildren(props.children).trim();
                            return (
                              <span 
                                className="markdown-element-with-indicator"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleQuickBranch(boldText, index);
                                }}
                                title={`Create branch: ${boldText}`}
                              >
                                <strong {...props} />
                              </span>
                            );
                          },
                        } : undefined}
                      >
                        {message.text}
                      </ReactMarkdown>
                      {hasMermaidBlock(message.text) && (
                        <MermaidRenderer 
                          code={extractMermaidCode(message.text) || ''}
                          messageId={`msg-${index}`}
                        />
                      )}
                      {!isMergeMessage && !isCopyMessage && clickablesEnabled && (listSummary.headers.length > 0 || listSummary.listItems.length > 0) && (
                        <div className="ai-list-summary">
                          {(listSummary.headers.length > 0
                            ? listSummary.headers
                            : listSummary.listItems
                          ).map((item, i: number) => (
                            <button
                              key={i}
                              className="ai-list-summary-item"
                              tabIndex={0}
                              type="button"
                              title={loadingBranchButtons.has(`${index}-${item.full}`) ? "Creating branch..." : `Create branch: ${item.full}`}
                              onClick={() => handleQuickBranch(item.full, index)}
                              disabled={loadingBranchButtons.has(`${index}-${item.full}`)}
                            >
                              {loadingBranchButtons.has(`${index}-${item.full}`) ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    style={{
                                      animation: 'spin 1s linear infinite'
                                    }}
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                  {item.display}
                                </div>
                              ) : (
                                item.display
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    message.text
                  )}
                </div>
                {message.suggestions && message.suggestions.length > 0 && (
                  <div className="suggestions">
                    {message.suggestions.map((suggestion, suggestionIndex) => (
                      <button
                        key={suggestionIndex}
                        className="suggestion-button"
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Branch and Copy-Merge buttons for AI messages (except the first one) */}
              {message.sender === 'ai' && index > 0 && selectedNote && (
                <div className="message-actions">
                  <button
                    className="branch-button"
                    onClick={() => handleBranchFromMessage(index)}
                    title="Branch from this message"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <circle cx="6" cy="18" r="2" fill="currentColor"/>
                      <circle cx="6" cy="6" r="2" fill="currentColor"/>
                      <circle cx="18" cy="18" r="2" fill="currentColor"/>
                      <path d="M6 8v8a4 4 0 004 4h4" stroke="currentColor" strokeWidth="2" fill="none"/>
                      <path d="M6 6c0-1.104.896-2 2-2h4a4 4 0 014 4v10" stroke="currentColor" strokeWidth="2" fill="none"/>
                    </svg>
                  </button>
                  {getExistingBranchesForMessage(index) > 0 && (
                    <span className="branch-count">
                      {getExistingBranchesForMessage(index)} branch{getExistingBranchesForMessage(index) > 1 ? 'es' : ''}
                    </span>
                  )}
                  
                  <button
                    className="copy-merge-button"
                    onClick={() => handleCopyMergeFromMessage(index)}
                    title="Copy this message to another note"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M8 4v12a2 2 0 002 2h8a2 2 0 002-2V7.242a2 2 0 00-.602-1.43L16.083 2.57A2 2 0 0014.685 2H10a2 2 0 00-2 2z" stroke="currentColor" strokeWidth="2" fill="none"/>
                      <path d="M16 18v2a2 2 0 01-2 2H6a2 2 0 01-2-2V9a2 2 0 012-2h2" stroke="currentColor" strokeWidth="2" fill="none"/>
                    </svg>
                  </button>

                </div>
              )}
                </div>
              </React.Fragment>
            );
        })}
        
        {/* Web Search Results Display */}
        {searchResults && (
          <div className={`search-results ${searchResultsExpanded ? 'expanded' : 'collapsed'}`}>
            <div 
              className="search-results-header"
              onMouseEnter={(e) => {
                const deleteBtn = e.currentTarget.querySelector('.search-results-delete-btn') as HTMLButtonElement;
                if (deleteBtn) {
                  deleteBtn.style.opacity = '1';
                  deleteBtn.style.backgroundColor = 'rgba(255, 255, 255, 1)';
                  deleteBtn.style.color = '#f44336';
                  deleteBtn.style.transform = 'scale(1.1)';
                }
              }}
              onMouseLeave={(e) => {
                const deleteBtn = e.currentTarget.querySelector('.search-results-delete-btn') as HTMLButtonElement;
                if (deleteBtn) {
                  deleteBtn.style.opacity = '0';
                  deleteBtn.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                  deleteBtn.style.color = '#666';
                  deleteBtn.style.transform = 'scale(1)';
                }
              }}
            >
              <div 
                className="search-results-title"
                onClick={() => setSearchResultsExpanded(!searchResultsExpanded)}
              >
                <h4>
                  🔍 Web Search Results
                  <span className="expand-icon">
                    {searchResultsExpanded ? ' ▼' : ' ▶'}
                  </span>
                </h4>
              </div>
              <button
                className="search-results-delete-btn"
                onClick={() => setSearchResults(null)}
                title="Remove search results"
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#666',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  opacity: 0,
                  zIndex: 10
                }}
              >
                ✕
              </button>
            </div>
            {searchResultsExpanded && (
              <>
                <p className="search-answer">{searchResults.answer}</p>
                <div className="search-links">
                  {searchResults.results.map((result: any, index: number) => (
                    <a 
                      key={index} 
                      href={result.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="search-link"
                    >
                      {result.title}
                    </a>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        
        {selectedNote && loadingNotes.has(selectedNote.id) && (
          <div className="message ai">
            <div className="message-content">
              <div className="loading-indicator">Thinking...</div>
            </div>
          </div>
        )}
      </div>

      <div className="side-chat-input with-web-search">
        
        {/* Files Section */}
        {selectedNote && selectedNote.attachedFiles && selectedNote.attachedFiles.length > 0 && (
          <div className="files-section">
            <div 
              className="files-header"
              onClick={toggleFilesExpanded}
            >
              <span className="files-icon">📎</span>
              <span className="files-text">
                Files ({selectedNote.attachedFiles.filter(f => f.uploadStatus === 'completed').length})
                {selectedNote.parentId && selectedNote.attachedFiles && selectedNote.attachedFiles.length > 0 && (
                  <span style={{ fontSize: '0.8em', color: '#666', marginLeft: '4px' }} title="Files inherited from parent note">
                    📥
                  </span>
                )}
                {selectedNote.attachedFiles && selectedNote.attachedFiles.filter(f => f.uploadStatus === 'failed').length > 0 && (
                  <span style={{ fontSize: '0.8em', color: '#f44336', marginLeft: '4px' }} title={`${selectedNote.attachedFiles.filter(f => f.uploadStatus === 'failed').length} failed upload(s) - click to view and dismiss`}>
                    ⚠️
                  </span>
                )}
              </span>
              <span className={`expand-icon ${filesExpanded ? 'expanded' : ''}`}>
                ▼
              </span>
            </div>
            
            {filesExpanded && (
              <div className="files-list">
                {selectedNote.attachedFiles.map((file) => (
                  <div key={file.id} className={`file-item ${file.uploadStatus}`}>
                    <div className="file-item-content">
                      <span className="file-icon">📄</span>
                      <span className="file-name" title={file.name}>
                        {file.name}
                      </span>
                      {file.uploadStatus === 'failed' && (
                        <>
                          <span className="file-status failed" title={file.error}>❌</span>
                          <button
                            className="file-remove"
                            onClick={() => handleRemoveFile(file.id)}
                            title="Remove failed file"
                          >
                            ✕
                          </button>
                        </>
                      )}
                      {file.uploadStatus === 'completed' && (
                        <button
                          className="file-remove"
                          onClick={() => handleRemoveFile(file.id)}
                          title="Remove file"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    {file.uploadStatus === 'uploading' && (
                      <div className="file-upload-progress-container">
                        <div className="file-upload-progress">
                          <div className="file-upload-progress-bar">
                            <div 
                              className="file-upload-progress-fill" 
                              style={{ width: `${uploadProgress[file.id] || 0}%` }}
                            />
                          </div>
                          <span className="file-upload-progress-text">{uploadProgress[file.id] || 0}%</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        <div className="input-container" style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          position: 'relative'
        }}>
          {/* Slim Web Search Bar - aligned with textarea */}
          <div className="web-search-bar" style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '0',
            padding: '4px 8px',
            background: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderBottom: 'none',
            borderRadius: '6px 6px 0 0',
            transition: 'all 0.2s ease',
            height: '28px',
            fontSize: '12px',
            width: '100%'
          }}>
            <div className="web-search-toggle" style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                color: '#6c757d',
                cursor: 'pointer',
                fontWeight: '400',
                userSelect: 'none',
                width: '100%'
              }}>
                <input
                  type="checkbox"
                  checked={webSearchEnabled}
                  onChange={(e) => setWebSearchEnabled(e.target.checked)}
                  disabled={selectedNote ? loadingNotes.has(selectedNote.id) : true}
                  style={{
                    width: '12px',
                    height: '12px',
                    accentColor: '#1976d2',
                    cursor: 'pointer',
                    margin: '0'
                  }}
                />
                <span className="web-search-label" style={{
                  fontSize: '12px',
                  color: webSearchEnabled ? '#1976d2' : '#6c757d',
                  fontWeight: webSearchEnabled ? '500' : '400'
                }}>🔍 Web Search</span>
              </label>
            </div>
          </div>
          
          {/* Textarea and Buttons Row */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '8px',
            position: 'relative'
          }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <textarea
              ref={inputRef}
              className={`side-chat-textarea ${isInputFocused ? 'expanded' : ''}`}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder="Type your message... (Shift+Enter for new line)"
              disabled={selectedNote ? loadingNotes.has(selectedNote.id) : true}
              rows={1}
              style={{ 
                minHeight: isInputFocused ? '80px' : '88px',
                transition: 'all 0.3s ease',
                width: '100%',
                padding: '12px 16px',
                paddingBottom: input.length > 0 ? '32px' : '12px', // Make room for character counter
                borderTop: 'none', // Always connect to web search bar
                borderLeft: isInputFocused ? '2px solid #1976d2' : '1px solid #e0e0e0',
                borderRight: isInputFocused ? '2px solid #1976d2' : '1px solid #e0e0e0',
                borderBottom: isInputFocused ? '2px solid #1976d2' : '1px solid #e0e0e0',
                borderRadius: '0 0 24px 24px', // Always rounded bottom, straight top
                fontSize: '14px',
                outline: 'none',
                resize: 'none',
                maxHeight: '120px',
                overflowY: 'auto',
                fontFamily: 'inherit',
                lineHeight: '1.4',
                boxSizing: 'border-box',
                boxShadow: isInputFocused ? '0 2px 8px rgba(25, 118, 210, 0.15)' : 'none'
              }}
            />
            
            {/* Character Counter */}
            {input.length > 0 && (
              <div style={{
                position: 'absolute',
                bottom: '8px',
                right: '12px',
                fontSize: '11px',
                color: input.length > (CHARACTER_LIMIT * 0.9) ? '#d32f2f' : input.length > (CHARACTER_LIMIT * 0.8) ? '#f57c00' : '#666',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                pointerEvents: 'none'
              }}>
                {input.length}/{CHARACTER_LIMIT}
              </div>
            )}
          </div>
          
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg,.gif,.bmp,.tiff"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />
          
          {/* Buttons Column */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            {/* File Upload Button */}
            <button
              onClick={handleFileUploadClick}
              disabled={!selectedNote || loadingNotes.has(selectedNote?.id || '')}
              title="Attach file (PDF, image)"
              style={{
                background: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: selectedNote && !loadingNotes.has(selectedNote.id) ? 'pointer' : 'not-allowed',
                color: selectedNote && !loadingNotes.has(selectedNote.id) ? '#666' : '#ccc',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                opacity: selectedNote && !loadingNotes.has(selectedNote.id) ? 1 : 0.5
              }}
              onMouseEnter={(e) => {
                if (selectedNote && !loadingNotes.has(selectedNote.id)) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.color = '#333';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedNote && !loadingNotes.has(selectedNote.id)) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.7)';
                  e.currentTarget.style.color = '#666';
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            >
              {/* Paper clip icon */}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            </button>
            
            {/* Send Button */}
            <button
              onClick={() => {
                if (input.trim() && selectedNote && !loadingNotes.has(selectedNote.id)) {
                  handleInputKeyDown({ key: 'Enter', shiftKey: false, preventDefault: () => {} } as React.KeyboardEvent<HTMLTextAreaElement>);
                }
              }}
              disabled={!input.trim() || !selectedNote || loadingNotes.has(selectedNote?.id || '')}
              title={loadingNotes.has(selectedNote?.id || '') ? "Sending message..." : "Send message (Enter)"}
              style={{
                background: input.trim() && selectedNote && !loadingNotes.has(selectedNote?.id || '') 
                  ? 'rgba(59, 130, 246, 0.9)' 
                  : loadingNotes.has(selectedNote?.id || '')
                    ? 'rgba(59, 130, 246, 0.7)'
                    : 'rgba(156, 163, 175, 0.5)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: input.trim() && selectedNote && !loadingNotes.has(selectedNote?.id || '') ? 'pointer' : 'not-allowed',
                color: 'white',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                opacity: (input.trim() && selectedNote) || loadingNotes.has(selectedNote?.id || '') ? 1 : 0.5
              }}
              onMouseEnter={(e) => {
                if (input.trim() && selectedNote && !loadingNotes.has(selectedNote?.id || '')) {
                  e.currentTarget.style.background = 'rgba(37, 99, 235, 0.9)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (input.trim() && selectedNote && !loadingNotes.has(selectedNote?.id || '')) {
                  e.currentTarget.style.background = 'rgba(59, 130, 246, 0.9)';
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            >
              {loadingNotes.has(selectedNote?.id || '') ? (
                /* Loading spinner */
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{
                    animation: 'spin 1s linear infinite'
                  }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              ) : (
                /* Send icon */
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Branch Creation Modal */}
      {branchModalOpen && selectedNote && (
        <BranchCreationModal
          isOpen={branchModalOpen}
          onClose={() => setBranchModalOpen(false)}
          onCreateBranch={handleCreateBranch}
          branchPoint={selectedBranchPoint}
          parentTopic={selectedNote.topic || 'New Topic'}
          messageContent={selectedMessageContent}
          isLoading={branchCreating}
          parentColor={selectedNote.color}
        />
      )}

      {/* Copy Merge Modal */}
      {copyMergeModalOpen && selectedNote && (
        <CopyMergeModal
          isOpen={copyMergeModalOpen}
          onClose={() => setCopyMergeModalOpen(false)}
          sourceNote={selectedNote}
          messageIndex={selectedCopyMergeMessageIndex}
          messageText={selectedCopyMergeMessageText}
          onCopyMerge={handleCopyMerge}
        />
      )}
    </div>

      {/* Delete Message Confirmation Dialog */}
      {showDeleteConfirm && ReactDOM.createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={handleCancelDeleteMessage}
        >
          <div 
            style={{
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              padding: '24px',
              borderRadius: '16px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              maxWidth: '400px',
              textAlign: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', color: '#1f2937', fontSize: '20px', fontWeight: '600' }}>
              Delete Message
            </h3>
            <p style={{ margin: '0 0 24px 0', color: '#6b7280', lineHeight: '1.5', fontSize: '14px' }}>
              Are you sure you want to delete this message? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={handleCancelDeleteMessage}
                style={{
                  padding: '10px 20px',
                  border: '1px solid rgba(209, 213, 219, 0.8)',
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  color: '#6b7280',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.borderColor = 'rgba(156, 163, 175, 0.8)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.7)';
                  e.currentTarget.style.borderColor = 'rgba(209, 213, 219, 0.8)';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeleteMessage}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '8px',
                  background: 'rgba(239, 68, 68, 0.9)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(220, 38, 38, 0.9)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.9)';
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Alert Modal */}
      {showAlertModal && (
        <AlertModal
          isOpen={showAlertModal}
          title={alertModalConfig.title}
          message={alertModalConfig.message}
          buttonText={alertModalConfig.buttonText}
          buttonVariant={alertModalConfig.buttonVariant}
          onClose={() => setShowAlertModal(false)}
        />
      )}

    </div>
    </>
  );
};

export default SideChatPane; 